import { useState } from 'react';
import CloseOutlined from '@mui/icons-material/CloseOutlined';
import {
  Avatar,
  Box,
  Dialog,
  IconButton,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import InfiniteScroll from 'react-infinite-scroll-component';
import AppButton from '../../../../components/ui/AppButton';
import { useTransferCompanyMember } from '../../../../hooks/useCompany';
import { useDebounceValue } from '../../../../hooks/useDebounceValue';
import { useGetUserInfinite } from '../../../../hooks/useUser';
import { useDialogStore } from '../../../../stores/dialogStore';

const AddUserDialog = ({ open, onClose, companyData }) => {
  const queryClient = useQueryClient();
  const [searchUser, setSearchUser] = useState('');
  const debounceSearchUser = useDebounceValue(searchUser, 500);
  const openDialog = useDialogStore((state) => state.openDialog);

  const {
    data: userListInfiniteData,
    fetchNextPage,
    hasNextPage,
  } = useGetUserInfinite({
    limit: 20,
    search: debounceSearchUser || undefined,
    enabled: open,
  });

  const users = userListInfiniteData?.pages.flatMap((page) => page?.Data?.data) ?? [];
  const { mutateAsync: transferCompanyMember } = useTransferCompanyMember();

  const handleClose = () => {
    onClose();
    setSearchUser('');
  };

  const handleAddNewUser = async (user) => {
    try {
      const response = await transferCompanyMember({
        dto: {
          code_company_destination: companyData.code,
          user_id_origin: user._id,
        },
      });

      if (response.Code === '20100') {
        openDialog({
          title: 'สำเร็จ',
          message: 'เพิ่มสมาชิกสำเร็จ',
          onCloseCallback: () => {
            queryClient.invalidateQueries({ queryKey: ['userList'] });
          },
        });
        onClose();
      }
    } catch (err) {
      console.error(err);
      openDialog({
        title: 'เกิดข้อผิดพลาด',
        message: err.response?.data?.Message || 'เกิดข้อผิดพลาดในการเพิ่มสมาชิก',
        variant: 'error',
      });
    }
  };

  return (
    <Dialog open={open} onClose={handleClose}>
      <Paper
        sx={{ minWidth: '500px', backgroundColor: 'white', padding: '2rem', position: 'relative' }}
      >
        <IconButton onClick={handleClose} sx={{ position: 'absolute', top: '0', right: '0' }}>
          <CloseOutlined />
        </IconButton>
        <Typography
          sx={{
            fontSize: '2.2rem',
            fontWeight: '700',
            color: '#002B38',
            textAlign: 'center',
            marginBottom: '20px',
          }}
        >
          ต้องการเพิ่มสมาชิก
        </Typography>
        <TextField
          id="addNewUser"
          placeholder="ค้นหาสมาชิกจาก ชื่อไลน์หรือเบอร์โทร"
          size="small"
          hiddenLabel
          fullWidth
          value={searchUser}
          onChange={(e) => setSearchUser(e.target.value || '')}
          sx={{
            '& .MuiInputBase-root': {
              fontSize: '1.5rem',
            },

            '& .MuiInputBase-input::placeholder': {
              fontSize: '1.5rem',
            },
          }}
        />
        <Box id="target" sx={{ height: '400px', overflow: 'auto', padding: '15px 0' }}>
          <InfiniteScroll
            dataLength={users.length}
            next={fetchNextPage}
            hasMore={!!hasNextPage}
            loader={<Box sx={{ fontSize: '16px', textAlign: 'center' }}>Loading...</Box>}
            scrollableTarget="target"
          >
            {users.map((item) => (
              <ListItem key={item.lineuserid} alignItems="center" sx={{ paddingLeft: '0' }}>
                <ListItemAvatar>
                  <Avatar src={item.pictureUrl} />
                </ListItemAvatar>
                <ListItemText
                  primary={item.displayName || item.lineuserid}
                  primaryTypographyProps={{ sx: { fontSize: '1.25rem' } }}
                />
                <AppButton
                  variant="text"
                  onClick={() => handleAddNewUser(item)}
                  sx={{
                    fontSize: '1.25rem',
                    fontWeight: '500',
                    height: '35px',
                    backgroundColor: '#e5e5e5',
                    color: '#171717',

                    '&:hover': {
                      backgroundColor: '#e5e5e5',
                      color: '#171717',
                      opacity: 0.85,
                    },
                  }}
                >
                  เพิ่ม
                </AppButton>
              </ListItem>
            ))}

            {users.length <= 0 && (
              <Box sx={{ fontSize: '1.25rem', textAlign: 'center' }}>ไม่พบรายชื่อ</Box>
            )}
          </InfiniteScroll>
        </Box>
      </Paper>
    </Dialog>
  );
};

export default AddUserDialog;
